import React, { useEffect, useState } from 'react';
import FilterBar from '../../components/FilterBar/FilterBar';
import { getProducts, getVendors } from '../../api/api';

const CONDITION = ['New', 'Refurb'];

export default function ProductManagementFilterBar({ onChange, value = {}, onNew }) {
    const [dropdowns, setDropdowns] = useState([
        { key: 'category', options: [{ value: '', label: 'Select Category' }] },
        { key: 'mfr', options: [{ value: '', label: 'Select MFR' }] },
        { key: 'vendor', options: [{ value: '', label: 'Select Vendor' }] },
        {
            key: 'condition',
            options: [{ value: '', label: 'Select Condition' }, ...CONDITION.map(c => ({ value: c, label: c }))]
        },
        {
            key: 'status',
            options: [
                { value: '', label: 'All Products' },
                { value: 'active', label: 'Active Products' },
                { value: 'archived', label: 'Archived Products' },
                { value: 'out_of_stock', label: 'Out of Stock' }
            ]
        },
    ]);

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                // Fetch ALL products for categories and MFRs (including archived and out of stock)
                // Use status="" to get all products, limit=1000 to get more products
                // We need to fetch in batches to get all products since default limit is 100
                let allProducts = [];
                let skip = 0;
                const limit = 1000; // Maximum allowed limit
                let hasMore = true;

                while (hasMore) {
                    // Fetch with status="" to get all products (active + archived)
                    // Note: This will still exclude stock_quantity=0 by default, so we need separate calls
                    const queryParams = new URLSearchParams({
                        status: '', // Empty string to get all products
                        limit: limit.toString(),
                        skip: skip.toString()
                    });
                    
                    const products = await getProducts(queryParams.toString());
                    if (!Array.isArray(products) || products.length === 0) {
                        hasMore = false;
                        break;
                    }
                    
                    allProducts = allProducts.concat(products);
                    
                    // If we got fewer than limit, we've reached the end
                    if (products.length < limit) {
                        hasMore = false;
                    } else {
                        skip += limit;
                    }
                }

                // Also fetch out of stock products to get their categories (with pagination)
                try {
                    let outOfStockSkip = 0;
                    let outOfStockHasMore = true;
                    const existingIds = new Set(allProducts.map(p => p.id));
                    
                    while (outOfStockHasMore) {
                        const outOfStockQuery = new URLSearchParams({
                            status: 'out_of_stock',
                            limit: limit.toString(),
                            skip: outOfStockSkip.toString()
                        });
                        const outOfStockProducts = await getProducts(outOfStockQuery.toString());
                        
                        if (!Array.isArray(outOfStockProducts) || outOfStockProducts.length === 0) {
                            outOfStockHasMore = false;
                            break;
                        }
                        
                        // Add out of stock products that aren't already in allProducts
                        const newOutOfStock = outOfStockProducts.filter(p => !existingIds.has(p.id));
                        allProducts = allProducts.concat(newOutOfStock);
                        
                        // Update existingIds for next iteration
                        newOutOfStock.forEach(p => existingIds.add(p.id));
                        
                        // If we got fewer than limit, we've reached the end
                        if (outOfStockProducts.length < limit) {
                            outOfStockHasMore = false;
                        } else {
                            outOfStockSkip += limit;
                        }
                    }
                } catch (outOfStockErr) {
                    console.warn('Error fetching out of stock products:', outOfStockErr);
                }

                if (allProducts.length === 0) return;

                const uniqueCategories = Array.from(
                    new Set(allProducts.map(p => p.category?.trim()).filter(Boolean))
                ).sort(); // Sort alphabetically for better UX
                const uniqueMfrs = Array.from(
                    new Set(allProducts.map(p => p.brand?.trim()).filter(Boolean))
                ).sort(); // Sort alphabetically for better UX

                // Fetch vendors from dedicated API endpoint (gets all unique vendors from DB)
                let uniqueVendors = [];
                try {
                    const vendors = await getVendors();
                    if (Array.isArray(vendors)) {
                        uniqueVendors = vendors.filter(v => v && v.trim()).map(v => v.trim());
                    }
                } catch (vendorErr) {
                    console.error('Error fetching vendors:', vendorErr);
                    // Fallback: extract from products if API fails
                    uniqueVendors = Array.from(
                        new Set(allProducts.map(p => p.vendor?.trim()).filter(Boolean))
                    ).sort();
                }

                setDropdowns([
                    {
                        key: 'category',
                        options: [{ value: '', label: 'Select Category' }, ...uniqueCategories.map(c => ({ value: c, label: c }))]
                    },
                    {
                        key: 'mfr',
                        options: [{ value: '', label: 'Select MFR' }, ...uniqueMfrs.map(m => ({ value: m, label: m }))]
                    },
                    {
                        key: 'vendor',
                        options: [{ value: '', label: 'Select Vendor' }, ...uniqueVendors.map(v => ({ value: v, label: v }))]
                    },
                    {
                        key: 'condition',
                        options: [{ value: '', label: 'Select Condition' }, ...CONDITION.map(c => ({ value: c, label: c }))]
                    },
                    {
                        key: 'status',
                        options: [
                            { value: '', label: 'All Products' },
                            { value: 'active', label: 'Active Products' },
                            { value: 'archived', label: 'Archived Products' },
                            { value: 'out_of_stock', label: 'Out of Stock' }
                        ]
                    },
                ]);
            } catch (err) {
                console.error('Error fetching products for filter bar:', err);
            }
        };

        fetchProductData();
    }, []);

    const handleFilterChange = (values) => {
        const normalizedCategory = values.category?.trim() || '';
        const normalizedMfr = values.mfr?.trim() || '';
        const normalizedVendor = values.vendor?.trim() || '';
        const normalizedCondition = values.condition?.trim() || '';
        const normalizedSearch = values.search ?? values.searchText ?? '';
        const normalizedStatus = values.status?.trim() || '';

        console.log('🔍 Filter values:', values);
        console.log('📦 Normalized vendor:', normalizedVendor);

        onChange?.({
            search: normalizedSearch,
            category: normalizedCategory,
            mfr: normalizedMfr,
            vendor: normalizedVendor,
            condition: normalizedCondition,
            status: normalizedStatus,
        });
    };

    return (
        <FilterBar
            dropdowns={dropdowns}
            onChange={handleFilterChange}
            showNewButton={!!onNew}
            onNew={onNew}
            placeholder="Name, email, etc..."
            value={{
                search: value.search ?? '',
                category: value.category ?? '',
                mfr: value.mfr ?? '',
                vendor: value.vendor ?? '',
                condition: value.condition ?? '',
                status: value.status ?? '',
            }}
        />
    );
}
